import { INationalPlace, NationalPlace } from '@app/models/NationalPlace'
import { bindFunctions } from '@app/utils/class'
import { makeAuthenticatedRequest } from '@app/utils/request'
import * as React from 'react'
import { ActionMeta, SingleValue } from 'react-select'
import Async from 'react-select/async'

interface INationalPlaceSelectorProps {
  className?: string
  name?: string
  onChange: (newValue?: NationalPlace) => void
  onInputChange: (input: string) => void
  placeholder?: string
  required?: boolean
  style?: React.CSSProperties
  value: string
}

interface INationalPlaceSelectorState {
  value?: NationalPlace
  valueString: string
}

export default class NationalPlaceSelector extends React.PureComponent<
  INationalPlaceSelectorProps,
  INationalPlaceSelectorState
> {
  private loadOptionsTimeout?: number
  private selectRef: React.RefObject<HTMLDivElement>

  constructor(props: INationalPlaceSelectorProps) {
    super(props)
    this.selectRef = React.createRef()
    bindFunctions(this)
    this.state = {
      valueString: this.props.value,
      value: this.props.value
        ? new NationalPlace({ cityState: this.props.value })
        : undefined,
    }
  }

  public componentDidMount() {
    this._checkValidity()
  }

  public componentDidUpdate() {
    this._checkValidity()
  }

  public componentWillUnmount() {
    if (this.loadOptionsTimeout) {
      window.clearTimeout(this.loadOptionsTimeout)
      this.loadOptionsTimeout = undefined
    }
  }

  public render() {
    return (
      <div
        className={this.props.className}
        style={this.props.style}
        ref={this.selectRef}
      >
        <Async
          className='react-select'
          key={'async-city-st-search' + this.state.valueString ?? ''}
          getOptionLabel={(opt) => opt.label}
          getOptionValue={(opt) => opt.value.id}
          defaultOptions
          loadOptions={this._getOptions}
          onChange={this._onChange}
          onFocus={(e) => {
            e.currentTarget.setSelectionRange(
              e.currentTarget.value.length,
              e.currentTarget.value.length,
            )
          }}
          // @ts-expect-error - defaultValue expects NationalPlace but we're providing a custom option shape
          defaultValue={
            this.props.value
              ? { label: this.props.value, value: this.state.value }
              : undefined
          }
          onMenuOpen={() => this.setState}
          defaultInputValue={this.props.value}
          placeholder={this.props.placeholder || 'City, ST'}
          required={this.props.required}
          onInputChange={(e, meta) => {
            if (meta.action !== 'menu-close' && meta.action !== 'input-blur') {
              this.props.onInputChange(e)
            }
            if (meta.action === 'input-change') {
              this.setState({ value: undefined })
            }
          }}
        />
      </div>
    )
  }

  private _checkValidity() {
    if (this.selectRef.current) {
      const inputElem = this.selectRef.current.querySelector(
        'input:invalid',
      ) as HTMLInputElement
      if (this.state.value) {
        this.selectRef.current.classList.remove('invalid')
      } else {
        this.selectRef.current.classList.add('invalid')
      }
    }
  }

  private _getOptions(
    input: string,
    callback: (
      options: Array<{
        value: NationalPlace
        label: string
      }>,
    ) => void,
  ) {
    if (this.loadOptionsTimeout) {
      window.clearTimeout(this.loadOptionsTimeout)
      this.loadOptionsTimeout = undefined
    }
    if (!input) {
      callback([])
      return
    }

    this.loadOptionsTimeout = window.setTimeout(() => {
      const queryUrl = `/api/NationalPlaces?q=${encodeURIComponent(input)}`
      return makeAuthenticatedRequest(queryUrl).then((result) => {
        if (result.success === true) {
          const options: Array<{
            value: NationalPlace
            label: string
          }> = result.results.map((np: INationalPlace) => {
            return {
              value: NationalPlace.fromJS(np),
              label: np.cityState,
            }
          })

          callback(options)
          return
        } else {
          callback([])
          return
        }
      })
    }, 250)
  }

  private _onChange(
    newValue: SingleValue<{ value: NationalPlace; label: string }>,
    meta: ActionMeta<{
      value: NationalPlace
      label: string
    }>,
  ) {
    if (meta.action === 'select-option') {
      this.setState({ value: newValue?.value })
      this.props.onChange(newValue?.value || undefined)
    }
  }
}
